import { addDays } from 'date-fns/addDays';
import { addMonths } from 'date-fns/addMonths';
import { addWeeks } from 'date-fns/addWeeks';
import { addYears } from 'date-fns/addYears';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { endOfDay } from 'date-fns/endOfDay';
import { endOfYear } from 'date-fns/endOfYear';
import { startOfDay } from 'date-fns/startOfDay';
import { startOfMonth } from 'date-fns/startOfMonth';
import { startOfWeek } from 'date-fns/startOfWeek';
import { startOfYear } from 'date-fns/startOfYear';

import type { CalendarMode } from '../define.js';
import type { CalendarRangeLoading } from './calendar-view-types.js';

const calendarWeekStartsOn = { weekStartsOn: 1 as const };

/**
 * A week fills the width of the week scroller: a day is a seventh of it beside the time axis
 * (calendar-view-styles `weekCanvas`). Below this width a day keeps it and the week scrolls.
 */
export const CALENDAR_SURFACE_WEEK_DAY_MIN_WIDTH = 120;
export const CALENDAR_SURFACE_WEEK_LOAD_MORE_DAYS = 14;
export const CALENDAR_SURFACE_TIME_AXIS_WIDTH = 56;

/** The width of a day in the week scroller, the same rule the stylesheet applies. */
export function calendarSurfaceWeekDayWidth(target: HTMLElement): number {
  return Math.max(
    CALENDAR_SURFACE_WEEK_DAY_MIN_WIDTH,
    (target.clientWidth - CALENDAR_SURFACE_TIME_AXIS_WIDTH) / 7
  );
}
export const CALENDAR_SURFACE_HOUR_HEIGHT = 48;
export const DEFAULT_CALENDAR_TIMED_SCROLL_MINUTE = 6 * 60;

export type CalendarSurfaceRange = {
  monthYearsAfter: number;
  monthYearsBefore: number;
  weekDaysAfter: number;
  weekDaysBefore: number;
};

export type CalendarSurfaceRangeLoadSession = {
  loading: CalendarRangeLoading;
  nextRange: CalendarSurfaceRange;
  previousScrollHeight?: number;
  target: HTMLElement;
};

export function createCalendarSurfaceRange(): CalendarSurfaceRange {
  return {
    monthYearsAfter: 0,
    monthYearsBefore: 0,
    weekDaysAfter: 21,
    weekDaysBefore: 0,
  };
}

export function calendarSurfaceDays(
  cursor: Date,
  mode: CalendarMode,
  range: CalendarSurfaceRange = createCalendarSurfaceRange()
): Date[] {
  if (mode === 'day') return [startOfDay(cursor)];
  if (mode === 'week') {
    const start = addDays(
      startOfWeek(cursor, calendarWeekStartsOn),
      -range.weekDaysBefore
    );
    return Array.from(
      { length: range.weekDaysBefore + range.weekDaysAfter },
      (_, index) => addDays(start, index)
    );
  }
  const start = startOfWeek(
    startOfYear(addYears(cursor, -range.monthYearsBefore)),
    calendarWeekStartsOn
  );
  const end = endOfYear(addYears(cursor, range.monthYearsAfter));
  const count = Math.ceil((differenceInCalendarDays(end, start) + 1) / 7) * 7;
  return Array.from({ length: count }, (_, index) => addDays(start, index));
}

export function calendarSurfaceWindow(
  cursor: Date,
  mode: CalendarMode,
  range: CalendarSurfaceRange = createCalendarSurfaceRange()
): { endAt: string; startAt: string } {
  const days = calendarSurfaceDays(cursor, mode, range);
  const first = days[0] ?? cursor;
  const last = days.at(-1) ?? first;
  return {
    endAt: endOfDay(last).toISOString(),
    startAt: startOfDay(first).toISOString(),
  };
}

export function shiftCalendarSurfaceCursor(
  cursor: Date,
  mode: CalendarMode,
  direction: -1 | 1
): Date {
  if (mode === 'day') return addDays(cursor, direction);
  if (mode === 'week') return addWeeks(cursor, direction);
  return addMonths(cursor, direction);
}

export function extendCalendarSurfaceRange(
  range: CalendarSurfaceRange,
  loading: CalendarRangeLoading
): CalendarSurfaceRange {
  if (loading.mode === 'week') {
    return loading.direction === 'previous'
      ? {
          ...range,
          weekDaysBefore:
            range.weekDaysBefore + CALENDAR_SURFACE_WEEK_LOAD_MORE_DAYS,
        }
      : {
          ...range,
          weekDaysAfter:
            range.weekDaysAfter + CALENDAR_SURFACE_WEEK_LOAD_MORE_DAYS,
        };
  }
  return loading.direction === 'previous'
    ? { ...range, monthYearsBefore: range.monthYearsBefore + 1 }
    : { ...range, monthYearsAfter: range.monthYearsAfter + 1 };
}

export function calendarSurfaceRangeLoadIntent(
  target: HTMLElement,
  mode: 'month' | 'week'
): CalendarRangeLoading | undefined {
  if (target.clientHeight <= 0 || target.clientWidth <= 0) return undefined;
  if (mode === 'week') {
    if (target.scrollWidth <= target.clientWidth + 1) return undefined;
    const rightDistance =
      target.scrollWidth - target.scrollLeft - target.clientWidth;
    if (target.scrollLeft < 180) return { direction: 'previous', mode };
    if (rightDistance < 240) return { direction: 'next', mode };
    return undefined;
  }
  if (target.scrollHeight <= target.clientHeight + 1) return undefined;
  const bottomDistance =
    target.scrollHeight - target.scrollTop - target.clientHeight;
  if (target.scrollTop < 180) return { direction: 'previous', mode };
  if (bottomDistance < 240) return { direction: 'next', mode };
  return undefined;
}

/**
 * Shared endless-scroll transition used by both the native database Calendar
 * view and aggregate Calendar adapters. It owns edge detection, range growth
 * and the measurements required to keep the viewport stationary.
 */
export function beginCalendarSurfaceRangeLoad(
  target: HTMLElement,
  mode: 'month' | 'week',
  range: CalendarSurfaceRange
): CalendarSurfaceRangeLoadSession | undefined {
  const loading = calendarSurfaceRangeLoadIntent(target, mode);
  if (!loading) return undefined;
  return {
    loading,
    nextRange: extendCalendarSurfaceRange(range, loading),
    previousScrollHeight:
      mode === 'month' && loading.direction === 'previous'
        ? target.scrollHeight
        : undefined,
    target,
  };
}

/** Restore the same visual position after prepending days or month rows. */
export function restoreCalendarSurfaceRangeScroll(
  session: CalendarSurfaceRangeLoadSession
): void {
  if (
    session.loading.mode === 'month' &&
    session.loading.direction === 'previous' &&
    session.previousScrollHeight !== undefined
  ) {
    session.target.scrollTop += Math.max(
      0,
      session.target.scrollHeight - session.previousScrollHeight
    );
  }
  if (
    session.loading.mode === 'week' &&
    session.loading.direction === 'previous'
  ) {
    session.target.scrollLeft +=
      CALENDAR_SURFACE_WEEK_LOAD_MORE_DAYS *
      calendarSurfaceWeekDayWidth(session.target);
  }
}

export function calendarSurfaceScrollCursor(
  target: HTMLElement,
  mode: 'month' | 'week',
  days: readonly Date[]
): Date | undefined {
  if (mode === 'week') {
    const contentCenterX =
      target.scrollLeft +
      target.clientWidth / 2 -
      CALENDAR_SURFACE_TIME_AXIS_WIDTH;
    const dayIndex = Math.max(
      0,
      Math.floor(contentCenterX / calendarSurfaceWeekDayWidth(target))
    );
    const day = days.at(dayIndex);
    return day ? startOfWeek(day, calendarWeekStartsOn) : undefined;
  }

  const cells = Array.from(
    target.querySelectorAll<HTMLElement>('[data-calendar-day]')
  );
  if (!cells.length) return undefined;
  const viewportCenter = target.scrollTop + target.clientHeight / 2;
  const centerCell =
    cells.find(cell => {
      const cellTop = cell.offsetTop;
      return (
        viewportCenter >= cellTop &&
        viewportCenter < cellTop + cell.offsetHeight
      );
    }) ?? cells.at(-1);
  const dayTime = Number(centerCell?.dataset.calendarDay);
  if (!Number.isFinite(dayTime)) return undefined;
  return startOfMonth(new Date(dayTime));
}

export function calendarSurfaceMonthAnchorKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}`;
}
